import { IBoardRound, IBoardSnapshot } from '../../../dtos/IBoardSnapshot';
import { diffSnapshots, EMPTY_DIFF, IBoardDiff } from './diff';

/**
 * Ordem do que aparece na tela quando chegam versões novas:
 *
 * 1. Cada rodada encerrada vira um banner, em fila, um por vez.
 * 2. Enquanto houver banner, a classificação continua a anterior — o público
 *    vê o resultado antes de ver a tabela mudar.
 * 3. Quando a fila esvazia, aplica a versão MAIS NOVA de uma vez (com os
 *    efeitos de subida, pontos e novo líder). Nunca anima um estado velho.
 *
 * O painel lateral (`latest`) não espera a fila: horário não é suspense.
 */
export interface IPresentationState {
  /** O que a classificação e o ticker mostram. */
  displayed: IBoardSnapshot | null;
  /** Versão mais nova recebida. */
  latest: IBoardSnapshot | null;
  queue: IBoardRound[];
  banner: IBoardRound | null;
  /** Efeitos da última aplicação na classificação. */
  effects: IBoardDiff;
  /** Muda a cada aplicação, para reiniciar animações. */
  effectsKey: number;
  /** Slots recém-iniciados (destaque no painel lateral). */
  started: string[];
}

export type TPresentationAction =
  | { type: 'receive'; snapshot: IBoardSnapshot }
  | { type: 'bannerDone' };

export const initialPresentation: IPresentationState = {
  displayed: null,
  latest: null,
  queue: [],
  banner: null,
  effects: EMPTY_DIFF,
  effectsKey: 0,
  started: [],
};

function commit(state: IPresentationState): IPresentationState {
  if (!state.latest || state.latest === state.displayed) return state;

  return {
    ...state,
    effects: diffSnapshots(state.displayed, state.latest),
    effectsKey: state.effectsKey + 1,
    displayed: state.latest,
  };
}

export function presentationReducer(
  state: IPresentationState,
  action: TPresentationAction,
): IPresentationState {
  if (action.type === 'receive') {
    const { snapshot } = action;

    if (!state.latest) {
      return { ...state, displayed: snapshot, latest: snapshot };
    }

    if (snapshot.version <= state.latest.version) return state;

    const change = diffSnapshots(state.latest, snapshot);
    const queue = [...state.queue, ...change.closedRounds];
    const next: IPresentationState = {
      ...state,
      latest: snapshot,
      started: change.startedSlots,
      queue,
    };

    if (next.banner) return next;

    if (queue.length > 0) {
      const [banner, ...rest] = queue;
      return { ...next, banner, queue: rest };
    }

    return commit(next);
  }

  if (action.type === 'bannerDone') {
    if (state.queue.length > 0) {
      const [banner, ...rest] = state.queue;
      return { ...state, banner, queue: rest };
    }

    return commit({ ...state, banner: null });
  }

  return state;
}
